import React, { useState } from "react";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { App } from "antd5";
import { debounce } from "lodash";

import { SendContactToCRM } from "components/integrations/SendContactToCRMButton";
import {
  getNoticePersonalisation,
  OutreachBuilderModal,
} from "components/outreach_builder/OutreachBuilderModal";
import PaywallPopover from "components/paywall/PaywallPopover";
import BuyerCategoriesWithPopover from "components/table_components/BuyerCategoriesWithPopover";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { ClipboardContent, copyToClipboard } from "lib/clipboard";
import {
  EllipsisTooltipRouterLink,
  EllipsisTooltipText,
} from "lib/core_components/EllipsisTooltip";
import RedactedWrapper, { RedactedSpan } from "lib/core_components/RedactedWrapper";
import { SHOW_ON_HOVER } from "lib/core_components/ShowOnHover";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventData, EventNames, logEvent, useTracking } from "lib/tracking";
import { LeadEntityType, PqlActionType } from "lib/types/graphQLEnums";
import { ContactResult } from "../../lib/hooks/api/contacts/useContacts";
import { InlineSaveLeadButton } from "../leads/saving/InlineSaveLeadButton";

import css from "./ContactsTableUtils.module.scss";

import blueGeometric from "../../../assets/images/icons/stotles_geometric_square.svg";

/**
 * Common tracking function for contacts data. Only logs data which is present for a contact and
 * unifies the attributes for tracking within the context of a contact
 * @param contact
 * @param eventData
 */
function trackContactData(contact: ContactResult, eventName: EventNames, eventData: EventData) {
  if (contact.jobFunction) {
    eventData["Contact function"] = contact.jobFunction.join(", ");
  }

  if (contact.seniority) {
    eventData["Contact seniority"] = contact.seniority;
  }

  if (contact.buyer?.name) {
    eventData["Buyer name"] = contact.buyer.name;
  }

  if (contact.jobTitle) {
    eventData["Contact job title"] = contact.jobTitle;
  }
  logEvent(eventName, eventData);
}

/**
 * Table column which has job title, contact's name & a button which appears upon hover that allows
 * user to begin outreach
 * @param param0
 * @returns
 */
export function ContactNameCell({
  contact,
  notice,
  rowIndex,
  setShowTooltipRowIndex,
  showTooltipRowIndex,
  showFirstCellActions,
}: {
  contact: ContactResult;
  rowIndex: number;
  notice?: NoticeDetails;
  setShowTooltipRowIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  showTooltipRowIndex?: number;
  showFirstCellActions: boolean;
}) {
  const { checkSubscription, authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": "Find contacts",
  });
  const { data: currentUser } = useCurrentUser();

  // State is used to show the lead save button on mouse entering the first cell
  const [mouseEntered, setMouseEntered] = useState(false);

  return (
    <div
      className={css.contactNameCell}
      onMouseEnter={() => setMouseEntered(true)}
      onMouseLeave={() => setMouseEntered(false)}
    >
      <RedactedWrapper
        requiredDataType={"CONTACTS"}
        contextSource="In-row"
        redactContent={
          <span onClick={checkSubscription} className={css.pointer}>
            <EllipsisTooltipText fullText={contact.jobTitle} containerClassname={css.rowHead} />
            <RedactedSpan
              textToRedact={`${contact.title} ${contact.firstName} ${contact.lastName}`}
            />
          </span>
        }
        // Just have this so that it doesn't call two modals
        showModalOnClick={false}
      >
        <div className={css.contactCellContent}>
          <EllipsisTooltipText fullText={contact.jobTitle} containerClassname={css.rowHead} />
          <EllipsisTooltipText
            fullText={`${contact.title} ${contact.firstName} ${contact.lastName}`}
            containerClassname={css.elipsisText}
          />
        </div>
      </RedactedWrapper>
      <div className={css.actions}>
        <InlineSaveLeadButton
          entityId={contact.id}
          entityType={LeadEntityType.Contact}
          displayType={mouseEntered ? "button" : "showSavedLeads"}
          savedLeads={contact.leads}
          defaultValues={{
            name: `${contact.firstName} ${contact.lastName}`,
            assignedToId: currentUser?.guid,
            entities: [
              {
                entityId: contact.id,
                entityType: LeadEntityType.Contact,
              },
              ...(contact.buyer
                ? [
                    {
                      entityId: contact.buyer.id,
                      entityType: LeadEntityType.Buyer,
                    },
                  ]
                : []),
            ],
          }}
        />
        <ContactTableActionButtons
          contact={contact}
          notice={notice}
          rowIndex={rowIndex}
          setShowTooltipRowIndex={setShowTooltipRowIndex}
          showTooltipRowIndex={showTooltipRowIndex}
          openHelper={checkSubscription}
          hasContacts={hasContacts}
          showFirstCellActions={showFirstCellActions}
        />
      </div>
    </div>
  );
}

/**
 * Action buttons within the contact name cell.If a user has access to contacts data and CRM integrations
 * it can allow users to create outreach and send contact to CRM. Otherwise we show a preview of the outreach modal if they don't have contacts data.
 * Also there's a tour for users who have not used outreach builder yet.
 * @param param0
 * @returns
 */
function ContactTableActionButtons({
  contact,
  notice,
  rowIndex,
  openHelper,
  hasContacts,
  setShowTooltipRowIndex,
  showTooltipRowIndex,
  showFirstCellActions,
}: {
  contact: ContactResult;
  rowIndex: number;
  notice?: NoticeDetails; // Also can be used to denote that we're in the related contacts page
  openHelper: () => boolean;
  hasContacts: boolean;
  setShowTooltipRowIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  showTooltipRowIndex?: number;
  showFirstCellActions: boolean;
}) {
  const outreachRef = React.useRef(null);
  const crmRef = React.useRef(null);
  const { trackingData } = useTracking();
  const contactsTrackingData = { ...trackingData, "CTA actioned": "Outreach" };
  const [isOutreachOpen, setIsOutreachOpen] = useState(false);

  const openOutreachModal = () => {
    if (!showFirstCellActions) {
      trackContactData(contact, EventNames.openOutreachModal, {});
      setIsOutreachOpen(true);
    }
  };

  const showFirstCell = showFirstCellActions && rowIndex === 0;
  const showContactActions = !hasContacts && rowIndex === showTooltipRowIndex;

  return (
    <div
      className={showFirstCell || showContactActions ? undefined : SHOW_ON_HOVER}
      // This tooltip is only for users who don't have access to contacts data
      onMouseEnter={() => setShowTooltipRowIndex(rowIndex)}
    >
      <div className={css.contactActions}>
        {hasContacts ? (
          <Button
            onClick={openOutreachModal}
            ref={outreachRef}
            icon="send"
            onMouseLeave={() => debounce(() => setShowTooltipRowIndex(undefined), 100)}
          >
            Outreach
          </Button>
        ) : (
          <PaywallPopover
            featureType="CONTACTS"
            showModalOnClick={false}
            contextSource={"In-row"}
            trackingData={contactsTrackingData}
          >
            <Button
              onClick={() => {
                if (!showFirstCellActions) openHelper();
              }}
              className={css.outreachButtonDisabled}
              icon="send"
              ref={outreachRef}
            >
              Outreach
            </Button>
          </PaywallPopover>
        )}
        <span ref={crmRef}>
          <SendContactToCRM contact={contact} showingTour={showFirstCellActions} />
        </span>
      </div>
      <OutreachBuilderModal
        isOpen={isOutreachOpen}
        contactEmail={contact.email || ""}
        contactName={`${contact.firstName} ${contact.lastName}`}
        jobTitle={contact.jobTitle}
        buyerName={contact.buyer?.name || ""}
        senderPersonalisation={notice ? getNoticePersonalisation(notice) : undefined}
        onClose={() => setIsOutreachOpen(false)}
      />
    </div>
  );
}

// Minimal buyer shape the cell needs which satisfied by both contacts tables' buyer field
export type BuyerCellOrganisation = {
  id: string;
  name: string;
  logoUrl?: string | null;
  categoryAssignments?: { categoryId: string; source: string }[] | null;
};

/**
 * Table cell with a buyer logo, a name linking to the buyer page, and the buyer-category
 * sub-line. Used by the legacy Oscar contacts table; the Cognism table builds its own
 * single-line buyer cell around {@link BuyerBadge}.
 */
export function BuyerDetailsCell({ buyer }: { buyer: BuyerCellOrganisation }) {
  return (
    <div className={css.buyerNameCell}>
      <div className={css.buyerLogoContainer}>
        <BuyerLogo url={buyer.logoUrl} />
      </div>
      <div>
        <EllipsisTooltipRouterLink
          fullText={buyer.name}
          linkText={buyer.name}
          linkProps={{ to: `/buyers/${buyer.id}`, className: css.marginBottom }}
          containerClassname={css.rowHead}
        />

        <BuyerCategoriesWithPopover
          size="default"
          buyerCategories={
            buyer.categoryAssignments?.map((c) => ({
              buyer_category_id: c.categoryId,
              source: c.source,
            })) ?? []
          }
          buyerName={buyer.name}
        />
      </div>
    </div>
  );
}

export function BuyerLogo({ url }: { url?: string | null }) {
  return url ? (
    <img src={url} height={24} width={24} className={css.buyerIcon} />
  ) : (
    <UIcon
      icon="wallet02"
      size={16}
      color="sysNeutralMuted"
      ariaLabel="Buyer logo placeholder"
      className={css.buyerIcon}
    />
  );
}

// 22px tile sized to the text line it sits against, with the design's 8px gap before the name.
const BADGE_TILE = { width: 22, height: 22, marginRight: 8, borderRadius: 4, flexShrink: 0 };

const BadgePlaceholder = styled.div(({ theme }) => ({
  ...BADGE_TILE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.colors.sysPrimarySubtle,
  // The design fades the glyph against the tile rather than using a lighter blue.
  svg: { opacity: 0.6 },
}));

// A logo can be any aspect ratio, so contain it rather than stretch it to the square.
const BadgeImage = styled.img({ ...BADGE_TILE, objectFit: "contain" });

/**
 * The buyer badge from the contacts design: the buyer's logo in a rounded tile, falling back
 * to a wallet glyph on subtle blue when there's no logo on file.
 */
export function BuyerBadge({ url }: { url?: string | null }) {
  if (url) {
    return <BadgeImage src={url} alt="" />;
  }
  return (
    <BadgePlaceholder>
      <UIcon
        icon="wallet02"
        size={14}
        color="sysPrimaryDefault"
        ariaLabel="Buyer logo placeholder"
      />
    </BadgePlaceholder>
  );
}

/**
 * Table column which has contact email + contact phone. Allows users with contacts data access to see the table rows &
 * copy their contents. Those without access have this information blurred like elsewhere in the app
 * @param param0
 * @returns
 */
export function ContactInformationCell({ contact }: { contact: ContactResult }) {
  const { message } = App.useApp();
  const handleCopyToClipboard = async (eventName: EventNames, copyContent?: string) => {
    if (copyContent) {
      const eventData: EventData = {
        "Copied value": copyContent,
      };

      trackContactData(contact, eventName, eventData);

      const content: ClipboardContent = { plain: copyContent };
      await copyToClipboard(content);

      void message.success("Copied!", 2);
    }
  };

  return (
    <div className={css.contactCellContent}>
      {contact.email ? (
        <Tooltip side="top" align="start" content="Copy email to clipboard">
          <span
            className={css.contactDetail}
            onClick={async () => handleCopyToClipboard(EventNames.emailCopied, contact.email || "")}
          >
            <span className={css.contactDetailContent}>
              <MailOutlined className={css.contactIcon} /> {contact.email}
            </span>
          </span>
        </Tooltip>
      ) : (
        <span className={css.contactDetail}>
          <MailOutlined className={css.contactIcon} /> <span className={css.emptyState}>--</span>
        </span>
      )}

      {contact.phone ? (
        <Tooltip side="top" align="start" content="Copy contact number to clipboard">
          <span
            className={css.contactDetail}
            onClick={() => handleCopyToClipboard(EventNames.phoneNumberCopied, contact.phone || "")}
          >
            <span className={css.contactDetailContent}>
              <PhoneOutlined className={css.contactIcon} />
              {contact.phone}
            </span>
          </span>
        </Tooltip>
      ) : (
        <span className={css.contactDetail}>
          <PhoneOutlined className={css.contactIcon} /> <span className={css.emptyState}>--</span>
        </span>
      )}
    </div>
  );
}

// The legacy table's empty state points users at the new contacts experience; registering
// interest sends a "Speak to your CSM" PQL alert to the account's CSM.
export function ContactsTableEmpty(): JSX.Element {
  const [interestRegistered, setInterestRegistered] = useState(false);
  const { mutate: actionPql, isLoading } = usePqlActioned({
    onSuccess: () => setInterestRegistered(true),
  });

  return (
    <div className={css.emptyStateScreen}>
      <img className={css.graphic} src={blueGeometric} />
      <h2 className={css.headerText}>No Contacts found</h2>
      <p className={css.normalText}>
        Our new contact search covers far more public sector decision-makers, at no extra cost.
      </p>
      <p className={css.normalText}>
        It isn&apos;t on for your account yet while we finalise updated terms.
      </p>
      {interestRegistered ? (
        <p className={css.normalText}>Thanks, your customer success manager will be in touch.</p>
      ) : (
        <Button
          loading={isLoading}
          onClick={() =>
            actionPql({
              actionType: PqlActionType.SpeakToYourCsm,
              dataType: "CONTACTS",
              pageUrl: window.location.href,
              contextSource: "Legacy contacts empty state",
            })
          }
        >
          Register interest
        </Button>
      )}
    </div>
  );
}
